import React, {useState} from 'react';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'});
}

function statusLabel(order) {
  if (order.hasReview) {
    return <span className="seller-reviews__badge seller-reviews__badge--positive">Reviewed</span>;
  }
  if (order.reviewRequestedAt) {
    return (
      <span className="seller-reviews__badge seller-reviews__badge--neutral">
        Requested {formatDate(order.reviewRequestedAt)}
      </span>
    );
  }
  return <span className="seller-reviews__badge seller-reviews__badge--neutral">Eligible</span>;
}

/**
 * Completed orders that are eligible for (or already have) a review request.
 * `onRequest` returns a promise resolving to the request outcome.
 */
export default function ReviewEligibleOrdersTable({orders, onRequest}) {
  const [busyUuid, setBusyUuid] = useState(null);
  const [rowError, setRowError] = useState({});

  const handleRequest = async order => {
    setBusyUuid(order.uuid);
    setRowError(previous => ({...previous, [order.uuid]: null}));
    try {
      await onRequest(order);
    } catch (error) {
      setRowError(previous => ({
        ...previous,
        [order.uuid]: error.message || 'Failed to request review.',
      }));
    } finally {
      setBusyUuid(null);
    }
  };

  if (orders.length === 0) {
    return <p className="seller-reviews__empty">No orders are currently eligible for a review request.</p>;
  }

  return (
    <table className="seller-reviews__table">
      <thead>
        <tr>
          <th scope="col">Order date</th>
          <th scope="col">Product</th>
          <th scope="col">Customer</th>
          <th scope="col">Status</th>
          <th scope="col"><span className="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        {orders.map(order => {
          const requested = Boolean(order.reviewRequestedAt);
          const canRequest = order.isEligible && !requested && !order.hasReview;
          return (
            <tr key={order.uuid}>
              <td>{formatDate(order.createdAt)}</td>
              <td>{order.product?.name || '—'}</td>
              <td>{order.customer?.nickname || 'Customer'}</td>
              <td>{statusLabel(order)}</td>
              <td>
                {canRequest ? (
                  <button
                    type="button"
                    className="button primary"
                    disabled={busyUuid === order.uuid}
                    onClick={() => handleRequest(order)}
                  >
                    {busyUuid === order.uuid ? 'Requesting…' : 'Request Review'}
                  </button>
                ) : (
                  <button type="button" className="button" disabled>
                    {order.hasReview ? 'Reviewed' : 'Review Requested'}
                  </button>
                )}
                {rowError[order.uuid] && (
                  <p className="flash error" role="alert">{rowError[order.uuid]}</p>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
